import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, Database, Clock, AlertTriangle, CheckCircle, 
  XCircle, RefreshCw, Server, Zap, HardDrive, Calendar,
  ChevronDown, ChevronUp, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StatusDot = ({ status }) => {
  const color = status === 'online' || status === 'healthy' || status === 'running'
    ? 'bg-emerald-500' : status === 'degraded' ? 'bg-amber-500' : 'bg-red-500';
  return (
    <span className="relative flex h-2.5 w-2.5">
      {(status === 'online' || status === 'healthy' || status === 'running') && (
        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full ${color} opacity-40`} />
      )}
      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${color}`} />
    </span>
  );
};

const ResponseBar = ({ ms }) => {
  const pct = Math.min(100, (ms / 100) * 100);
  const color = ms < 20 ? 'bg-emerald-500' : ms < 50 ? 'bg-amber-400' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 tabular-nums w-12 text-right">{ms}ms</span>
    </div>
  );
};

const SystemHealthDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);

  const fetchHealth = useCallback(async (showToast = false) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/system-health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      setLastRefresh(new Date());
      if (showToast) toast.success('System-Status aktualisiert');
    } catch (err) {
      console.error('Health check failed:', err);
      if (showToast) toast.error('Fehler beim Laden des System-Status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchHealth(), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchHealth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="health-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-16 text-gray-500" data-testid="health-error">
        <XCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
        <p>System-Status konnte nicht geladen werden</p>
        <Button className="mt-4" onClick={() => { setLoading(true); fetchHealth(true); }}>Erneut versuchen</Button>
      </div>
    );
  }

  const isHealthy = data.overall_status === 'healthy';
  const onlineModules = data.modules.online;
  const totalModules = data.modules.total;
  const modulePct = Math.round((onlineModules / totalModules) * 100);

  return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="system-health-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${isHealthy ? 'bg-emerald-50' : 'bg-amber-50'}`}>
            <Shield className={`w-6 h-6 ${isHealthy ? 'text-emerald-600' : 'text-amber-600'}`} />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900" data-testid="health-title">System Health</h1>
            <p className="text-sm text-gray-500">
              {lastRefresh && `Letztes Update: ${lastRefresh.toLocaleTimeString('de-CH')}`}
              {autoRefresh && ' (Auto-Refresh 30s)'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={autoRefresh ? 'default' : 'outline'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="toggle-auto-refresh"
            className={autoRefresh ? 'bg-teal-600 hover:bg-teal-700' : ''}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto AN' : 'Auto AUS'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => fetchHealth(true)} data-testid="manual-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Jetzt prüfen
          </Button>
        </div>
      </div>

      {/* Overall status banner */}
      <div className={`rounded-xl p-4 flex items-center gap-3 ${isHealthy ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="overall-status">
        <StatusDot status={data.overall_status} />
        <span className={`font-semibold ${isHealthy ? 'text-emerald-800' : 'text-amber-800'}`}>
          {isHealthy ? 'Alle Systeme operativ' : 'Einige Systeme beeinträchtigt'}
        </span>
        <Badge variant="secondary" className="ml-auto">{data.total_check_ms}ms total</Badge>
      </div>

      {/* Top cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Modules */}
        <Card className="border-0 shadow-sm" data-testid="card-modules">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                <Server className="w-4 h-4" /> Module
              </div>
              <span className="text-lg font-bold text-gray-900">{onlineModules}/{totalModules}</span>
            </div>
            <Progress value={modulePct} className="h-2" />
            <p className="text-xs text-gray-500 mt-2">Ø {data.modules.avg_response_ms}ms Antwortzeit</p>
          </CardContent>
        </Card>

        {/* Database */}
        <Card className="border-0 shadow-sm" data-testid="card-database">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                <Database className="w-4 h-4" /> Datenbank
              </div>
              <StatusDot status={data.database.status} />
            </div>
            <p className="text-lg font-bold text-gray-900">{data.database.ping_ms}ms</p>
            <p className="text-xs text-gray-500 mt-1">{data.database.total_documents.toLocaleString()} Dokumente / {data.database.size_mb} MB</p>
          </CardContent>
        </Card>

        {/* Scheduler */}
        <Card className="border-0 shadow-sm" data-testid="card-scheduler">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                <Clock className="w-4 h-4" /> Scheduler
              </div>
              <StatusDot status={data.scheduler.status} />
            </div>
            <p className="text-lg font-bold text-gray-900">{data.scheduler.active_jobs}/{data.scheduler.total_jobs} Jobs</p>
            <p className="text-xs text-gray-500 mt-1">Alle Cron-Jobs aktiv</p>
          </CardContent>
        </Card>

        {/* Errors */}
        <Card className="border-0 shadow-sm" data-testid="card-errors">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                <AlertTriangle className="w-4 h-4" /> Fehler
              </div>
              {data.recent_errors.length === 0
                ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                : <Badge variant="destructive">{data.recent_errors.length}</Badge>}
            </div>
            <p className="text-lg font-bold text-gray-900">
              {data.recent_errors.length === 0 ? 'Keine' : data.recent_errors.length}
            </p>
            <p className="text-xs text-gray-500 mt-1">Letzte 500er Fehler</p>
          </CardContent>
        </Card>
      </div>

      {/* Module grid */}
      <Card className="border-0 shadow-sm" data-testid="modules-table">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="w-4 h-4 text-teal-600" /> API-Module ({onlineModules}/{totalModules} online)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {data.modules.details.map((m) => (
              <div
                key={m.name}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  m.status === 'online' ? 'border-gray-100 hover:border-emerald-200 bg-white' : 'border-red-200 bg-red-50'
                }`}
                data-testid={`module-${m.collection}`}
              >
                <StatusDot status={m.status} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                  <p className="text-xs text-gray-400">{m.collection}</p>
                </div>
                <ResponseBar ms={m.response_ms} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Scheduler jobs */}
      <Card className="border-0 shadow-sm" data-testid="scheduler-section">
        <CardHeader className="pb-3 cursor-pointer" onClick={() => setSchedulerOpen(!schedulerOpen)}>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-600" /> Scheduler-Jobs ({data.scheduler.active_jobs} aktiv)
            </span>
            {schedulerOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </CardTitle>
        </CardHeader>
        {schedulerOpen && (
          <CardContent>
            <div className="space-y-1.5">
              {data.scheduler.jobs.map((job, idx) => (
                <div key={idx} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors" data-testid={`job-${idx}`}>
                  <div className="flex items-center gap-2.5">
                    <StatusDot status={job.active ? 'online' : 'error'} />
                    <span className="text-sm text-gray-800">{job.name}</span>
                  </div>
                  <span className="text-xs text-gray-500 tabular-nums">
                    {job.next_run ? new Date(job.next_run).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Recent errors */}
      {data.recent_errors.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-red-400" data-testid="errors-section">
          <CardHeader className="pb-3 cursor-pointer" onClick={() => setErrorsOpen(!errorsOpen)}>
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-4 h-4" /> Letzte Fehler ({data.recent_errors.length})
              </span>
              {errorsOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </CardTitle>
          </CardHeader>
          {errorsOpen && (
            <CardContent>
              <div className="space-y-1.5">
                {data.recent_errors.map((err, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2.5 rounded-lg bg-red-50" data-testid={`error-${idx}`}>
                    <Badge variant="destructive">{err.status_code}</Badge>
                    <span className="text-sm text-gray-700 font-mono">{err.method} {err.path}</span>
                    <span className="ml-auto text-xs text-gray-500">{err.timestamp ? new Date(err.timestamp).toLocaleString('de-CH') : ''}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default SystemHealthDashboard;
